import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { ResultOf } from "@graphql-typed-document-node/core"
import { graphqlClient } from "@/lib/graphql"
import { graphql } from "@/__generated__"

const LIST_QUERY = graphql(`
  query MyTeachingMaterials {
    myTeachingMaterials {
      id
      title
      filename
      contentType
      byteSize
      documentUrl
      createdAt
    }
  }
`)

// Derived from the LIST_QUERY document so the shape can't drift from the schema.
export type TeachingMaterial = ResultOf<typeof LIST_QUERY>["myTeachingMaterials"][number]

const RENAME_MUTATION = graphql(`
  mutation RenameTeachingMaterial($input: RenameTeachingMaterialInput!) {
    renameTeachingMaterial(input: $input) {
      teachingMaterial { id title }
      errors
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeleteTeachingMaterial($input: DeleteTeachingMaterialInput!) {
    deleteTeachingMaterial(input: $input) {
      deletedId
      errors
    }
  }
`)

const QUERY_KEY = ["teachingMaterials"]

export function useTeachingMaterials() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await graphqlClient.request(LIST_QUERY)
      return res.myTeachingMaterials
    },
  })
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

// Plain POST (not GraphQL): the controller checks the CSRF token before it
// looks for the signed-in user, so without the header the tutor reads as signed out.
// A failure with no message from the server (a 401 or 500 has no body) throws an
// empty message: the caller shows its translated text instead.
export async function uploadMaterial({ file, title }: { file: File; title?: string }) {
  const form = new FormData()
  form.append("file", file)
  if (title) form.append("title", title)
  const res = await fetch("/tutor/teaching_materials", {
    method: "POST",
    body: form,
    credentials: "same-origin",
    headers: { "X-CSRF-Token": getCsrfToken() },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.errors?.[0] ?? "")
  }
  return res.json()
}

export function useUploadMaterial() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadMaterial,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useRenameMaterial() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const res = await graphqlClient.request(
        RENAME_MUTATION,
        { input: { id, title } },
      )
      const errors = res.renameTeachingMaterial.errors
      if (errors.length > 0) throw new Error(errors[0])
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useDeleteMaterial() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await graphqlClient.request(
        DELETE_MUTATION,
        { input: { id } },
      )
      const errors = res.deleteTeachingMaterial.errors
      if (errors.length > 0) throw new Error(errors[0])
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function formatBytes(bytes: number | null): string {
  if (!bytes) return "—"
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
